import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Paper, Typography, Button, Radio, RadioGroup, FormControl, FormControlLabel, FormLabel, TextField, CircularProgress, Alert, Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const HtmlQuiz = ({ level: initialLevel }) => {
    const { t, i18n } = useTranslation(); // Initialize translation and get the language
    const [quizQuestions, setQuizQuestions] = useState([]);
    const [level, setLevel] = useState(initialLevel || 1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [score, setScore] = useState(0);
    const [isQuizCompleted, setIsQuizCompleted] = useState(false);
    const [quizResultMessage, setQuizResultMessage] = useState(null);

    // Fetch quiz questions based on language and level
    useEffect(() => {
        const fetchHtmlQuizQuestions = async () => {
            try {
                setLoading(true);
                const lang = i18n.language; // Get the active language
                // Determine the URL based on language and level
                let url;
                if (lang === 'en') {
                    url = `https://learningplatformbc-production.up.railway.app/api/levels/html/level${level}.json`;
                } else {
                    url = `https://learningplatformbc-production.up.railway.app/api/levels/translations/html/level${level}_${lang}.json`;
                }
                console.log(lang)
                console.log(url)

                
                // Fetch quiz data from API
                const response = await axios.get(url);
                setQuizQuestions(response.data || []);
                setLoading(false);
                setIsQuizCompleted(false);
                setQuizResultMessage(null);
            } catch (error) {
                console.error('Error fetching HTML quiz questions:', error);
                setError(t('quiz.errorLoading'));
                setQuizQuestions([]);
                setLoading(false);
            }
        };

        fetchHtmlQuizQuestions();
    }, [level, t, i18n.language]); // Re-fetch when language or level changes

    // Handle user selecting an answer
    const handleAnswerChange = (questionIndex, answer) => {
        setSelectedAnswers({
            ...selectedAnswers,
            [questionIndex]: answer
        });
    };

    // Handle the quiz completion, calculating score and updating state
    const handleQuizCompletion = () => {
        let correctAnswersCount = 0;
        quizQuestions.forEach((question, index) => {
            if (selectedAnswers[index] === question.answer) {
                correctAnswersCount += 1;
            }
        });

        const percentage = (correctAnswersCount / quizQuestions.length) * 100;
        setScore(percentage);
        setIsQuizCompleted(true);

        if (percentage >= 70) {
            setLevel(level + 1);
            setSelectedAnswers({});
            setQuizResultMessage({ type: 'success', text: t('quiz.passMessage') });
        } else {
            setQuizResultMessage({ type: 'error', text: t('quiz.failMessage') });
        }
    };

    // Handle loading and error states
    if (loading) return <CircularProgress />;
    if (error) return <Typography color="error">{error}</Typography>;

    // Render the quiz form
    return (
        <Paper style={{ padding: 20, marginTop: 20, maxWidth: 600, margin: 'auto' }}>
            <Typography variant="h4" gutterBottom>
                {t('HTML')} - {t('quiz.level', { level })}
            </Typography>
            {quizQuestions.length === 0 ? (
                <Typography>{t('quiz.noQuestions')}</Typography>
            ) : (
                <form>
                    <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
                        {quizQuestions.map((question, index) => (
                            <li key={index} style={{ marginBottom: 20 }}>
                                <FormControl component="fieldset" fullWidth>
                                    <FormLabel component="legend" style={{ marginBottom: 10, fontWeight: 'bold' }}>
                                        {question.question}
                                    </FormLabel>
                                    {question.type === 'multiple-choice' ? (
                                        <RadioGroup
                                            aria-label={`question-${index}`}
                                            name={`question-${index}`}
                                            value={selectedAnswers[index] || ''}
                                            onChange={(e) => handleAnswerChange(index, e.target.value)}
                                        >
                                            {question.options && question.options.map((option, optionIndex) => (
                                                <Box
                                                    key={optionIndex}
                                                    component="div"
                                                    style={{
                                                        border: '1px solid #ccc',
                                                        borderRadius: '8px',
                                                        padding: '10px 15px',
                                                        marginBottom: '10px',
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.3s',
                                                        backgroundColor: selectedAnswers[index] === option ? 'dodgerblue' : 'white',
                                                        color: selectedAnswers[index] === option ? 'white' : 'black',
                                                    }}
                                                    onClick={() => handleAnswerChange(index, option)}
                                                >
                                                    <FormControlLabel
                                                        value={option}
                                                        control={<Radio style={{ display: 'none' }} />}
                                                        label={option}
                                                        style={{ width: '100%', cursor: 'pointer' }}
                                                    />
                                                </Box>
                                            ))}
                                        </RadioGroup>
                                    ) : question.type === 'text-input' ? (
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            placeholder={t('quiz.placeholder')}
                                            value={selectedAnswers[index] || ''}
                                            onChange={(e) => handleAnswerChange(index, e.target.value)}
                                            style={{ marginTop: 10 }}
                                        />
                                    ) : null}
                                </FormControl>
                            </li>
                        ))}
                    </ul>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleQuizCompletion}
                        disabled={isQuizCompleted}
                        style={{ marginTop: 20 }}
                    >
                        {t('quiz.completeButton')}
                    </Button>
                    {isQuizCompleted && (
                        <Typography variant="h6" style={{ marginTop: 20 }}>
                            {t('quiz.score', { score })}
                        </Typography>
                    )}
                    {quizResultMessage && (
                        <Alert severity={quizResultMessage.type} style={{ marginTop: 20 }}>
                            {quizResultMessage.text}
                        </Alert>
                    )}
                </form>
            )}
        </Paper>
    );
};

export default HtmlQuiz;
